import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, IconButton } from '@cbrebuild/blocks';
import userEventService from '../../services/user-event-service';
import attachmentLookupService from '../../services/attachment-lookup-service';
import './attachment-lookup-table.scss';

/**
 * Table component for displaying attachment lookup results
 * Uses attachment-lookup-service for downloads
 */

// Helper to flatten new backend data structure
const flattenData = (data) => {
  if (!Array.isArray(data)) return [];
  const rows = [];
  data.forEach(deal => {
    const { deal_id, deal_type, voucher_ids, submission_dates, attachments } = deal;
    if (Array.isArray(attachments)) {
      attachments.forEach(att => {
        rows.push({
          ...att,
          deal_id,
          deal_type,
          voucher_id: att.voucher_id || (Array.isArray(voucher_ids) ? voucher_ids[0] : voucher_ids),
          submission_date: att.submission_date || (Array.isArray(submission_dates) ? submission_dates[0] : submission_dates),
        });
      });
    }
  });
  return rows;
};

const AttachmentLookupTable = ({ data, toast, onCancel, uploadedFile }) => {
  // No selection limit for bulk download
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmittingJob, setIsSubmittingJob] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Flatten new data structure for table rendering
  const flatData = useMemo(() => flattenData(data), [data]);

  // Calculate statistics
  const uniqueVoucherIds = new Set(flatData.filter(d => d.voucher_id).map(d => d.voucher_id)).size;
  const totalAttachments = flatData.filter(d => d.attachment_id).length;

  // Filter data based on search term - memoized for performance
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return flatData;
    }
    const search = searchTerm.trim().toLowerCase();
    return flatData.filter(item => {
      const voucherId = (item.voucher_id || '').toString().toLowerCase();
      const dealId = (item.deal_id || '').toString().toLowerCase();
      // Exact match for voucher ID or deal ID
      return voucherId === search || dealId === search;
    });
  }, [flatData, searchTerm]);

  // Handle row selection (no limit)
  const handleSelectRow = (attachmentId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(attachmentId)) {
      newSelected.delete(attachmentId);
    } else {
      newSelected.add(attachmentId);
    }
    setSelectedIds(newSelected);
  };

  // Handle select all (no limit)
  const handleSelectAll = () => {
    const availableAttachments = filteredData.filter(d => d.attachment_id);
    if (selectedIds.size > 0) {
      // Deselect all
      setSelectedIds(new Set());
    } else {
      // Select all attachments
      const toSelect = availableAttachments.map(d => d.attachment_id);
      setSelectedIds(new Set(toSelect));
    }
  };

  // Download single attachment using attachment lookup service
  const handleDownloadSingle = (attachmentId, name) => {
    attachmentLookupService.downloadSingleAttachment(attachmentId)
      .then(() => {
        toast(`Downloading ${name}`, 'success');
        userEventService.trackEvent({
          eventCategory: 'Attachment Lookup',
          eventAction: 'single_attachment_download',
          eventLabel: 'attachment_lookup',
        });
      })
      .catch((error) => {
        toast(error.message || `Failed to download ${name}`, 'error');
      });
  };

  // Submit async bulk download job
  const handleBulkDownload = async () => {
    if (selectedIds.size === 0) {
      toast('Please select at least one attachment to download.', 'error');
      return;
    }
    setIsSubmittingJob(true);
    try {
      // Send full filename with extension to backend
      const uploadFilename = uploadedFile && uploadedFile.name ? uploadedFile.name : 'bulk_download';
      
      // Call new async job endpoint with full filename (backend will handle extension removal)
      const response = await attachmentLookupService.createBulkDownloadJob(
        Array.from(selectedIds),
        uploadFilename
      );
      if (response && response.job_id) {
        toast('Bulk download job submitted! It will be available in the download center shortly.', 'success');
      } else {
        throw new Error('Failed to submit download job');
      }
    } catch (error) {
      toast(error.message || 'Failed to submit download job', 'error');
    } finally {
      setIsSubmittingJob(false);
    }
  };

  // Export data to Excel
  const handleExportData = async () => {
    if (!uploadedFile) {
      toast('Upload file not available. Please upload the file again.', 'error');
      return;
    }
    setIsExporting(true);
    try {
      const response = await attachmentLookupService.exportDataToExcel(uploadedFile);
      userEventService.trackEvent({
        eventCategory: 'Attachment Lookup',
        eventAction: 'data_export',
        eventLabel: 'attachment_lookup_excel_export',
      });
      if (response.success) {
        toast('Successfully exported data to Excel', 'success');
      }
    } catch (error) {
      toast(error.message || 'Failed to export data', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="attachment-lookup-table">
      <div className="attachment-lookup-header">
        <div className="attachment-lookup-info">
          <h2>Attachment Results</h2>
          <div className="attachment-stats-row">
            <div className="stat-card">
              <span className="stat-label">Voucher IDs</span>
              <span className="stat-value">{uniqueVoucherIds}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Attachments</span>
              <span className="stat-value">{totalAttachments}</span>
            </div>
            {searchTerm && (
              <div className="stat-card stat-card-search">
                <span className="stat-label">Matching Search</span>
                <span className="stat-value">{filteredData.length}</span>
              </div>
            )}
            <div className="attachment-lookup-actions">
              <Button
                onClick={onCancel}
                kind="secondary"
                disabled={false}
              >
                <Icon name="upload" />
                Upload File
              </Button>
              <Button
                onClick={handleExportData}
                kind="secondary"
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <Icon name="loading" spin /> Exporting...
                  </>
                ) : (
                  <>
                    <Icon name="download" />
                    Export Data
                  </>
                )}
              </Button>
              <Button
                onClick={handleBulkDownload}
                disabled={selectedIds.size === 0 || isSubmittingJob}
                kind="primary"
                title={selectedIds.size === 0 ? 'Select attachments to download' : `Download ${selectedIds.size} selected attachment${selectedIds.size !== 1 ? 's' : ''}`}
              >
                {isSubmittingJob ? (
                  <>
                    <Icon name="loading" spin /> Submitting...
                  </>
                ) : (
                  <>
                    <Icon name="download" />
                    Bulk Download ({selectedIds.size})
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-pool-comparison-table-wrapper">
        {/* Search bar directly above table */}
        <div className="table-search-bar">
          <Icon name="search" />
          <input
            type="text"
            placeholder="Search by Voucher ID or Deal ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="admin-pool-comparison-table-container">
          <table className="admin-pool-comparison-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedIds.size > 0 && selectedIds.size === filteredData.filter(d => d.attachment_id).length}
                    onChange={handleSelectAll}
                    title={"Select all attachments"}
                  />
                </th>
                <th>Voucher ID</th>
                <th>Deal ID</th>
                <th>Deal Type</th>
                <th>Submission Date</th>
                <th>Document Name</th>
                <th>On Voucher</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                    {searchTerm ? 'No results found matching your search.' : 'No data available.'}
                  </td>
                </tr>
              ) : (
                filteredData.map((item, idx) => (
                <tr key={`${item.voucher_id}-${item.attachment_id || 'no-attach'}-${idx}`}>
                  <td>
                    {item.attachment_id && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.attachment_id)}
                        onChange={() => handleSelectRow(item.attachment_id)}
                      />
                    )}
                  </td>
                  <td>{item.voucher_id || '-'}</td>
                  <td>{item.deal_id || '-'}</td>
                  <td>{item.deal_type || '-'}</td>
                  <td>
                    {item.submission_date 
                      ? new Date(item.submission_date).toLocaleDateString() 
                      : '-'}
                  </td>
                  <td>{item.document_name || 'No attachments'}</td>
                  <td>{item.on_voucher_submission || '-'}</td>
                  <td>
                    {item.attachment_id && (
                      <IconButton
                        onClick={() => handleDownloadSingle(item.attachment_id, item.document_name)}
                        iconName="download"
                        variant="basic"
                        title="Download"
                      />
                    )}
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

AttachmentLookupTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    voucher_id: PropTypes.string,
    deal_id: PropTypes.number,
    deal_type: PropTypes.string,
    submission_date: PropTypes.string,
    document_name: PropTypes.string,
    attachment_file_key: PropTypes.string,
    on_voucher_submission: PropTypes.string,
    attachment_id: PropTypes.number,
  })).isRequired,
  toast: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  uploadedFile: PropTypes.object
};

export default AttachmentLookupTable;
